import * as React from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  compareByFirstName,
  compareByLastName,
  splitPersonName,
  describePhotoExclusions,
  hasDayChoice,
  isBookedForSession,
  sessionDayLabel,
  type AttendanceSheet,
  type AttendanceStatus,
  type AttendanceStudentReport,
  type MarkAttendanceInput,
  type Program,
} from "@alight/contracts";
import {
  Button,
  Card,
  CardContent,
  cn,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@alight/ui";
import {
  AlertTriangle,
  CalendarCheck,
  CalendarPlus,
  CameraOff,
  Check,
  Download,
  Maximize2,
  UserPlus,
} from "lucide-react";
import { ManualEnrolmentDialog } from "../components/ManualEnrolmentDialog";

const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "";

/**
 * ATTENDANCE — mark the weekly roll for a running program.
 *
 * Each week of a program is its own lesson record (auto-generated forward while
 * the program is active); attendance is stored one row per enrolled child per
 * lesson. This screen lets the host pick a running program, jump to a lesson
 * (newest first by default), and mark each child. A per-program Report tab
 * aggregates the marks into attendance rates.
 */

/** Status order + colours. Full class strings (no interpolation) so Tailwind
 *  keeps them. `here`/`late` count as attended for the rate. */
const STATUS_ORDER = ["here", "late", "away", "sick", "holiday"] as const;

/** Each status owns a colour. Applied as INLINE STYLES (not Tailwind classes)
 *  so the colours always render — they don't depend on the palette surviving
 *  the CSS build. `solid` = selected button; `soft` = pale tint (unselected
 *  button, grid cell, badge). */
interface StatusStyle {
  label: string;
  short: string;
  solidBg: string;
  softBg: string;
  softFg: string;
  softBorder: string;
}
const STATUS_META: Record<AttendanceStatus, StatusStyle> = {
  here: { label: "Here", short: "H", solidBg: "#16a34a", softBg: "#dcfce7", softFg: "#166534", softBorder: "#86efac" },
  late: { label: "Late", short: "L", solidBg: "#d97706", softBg: "#fef3c7", softFg: "#92400e", softBorder: "#fcd34d" },
  away: { label: "Away", short: "A", solidBg: "#dc2626", softBg: "#fee2e2", softFg: "#991b1b", softBorder: "#fca5a5" },
  sick: { label: "Sick", short: "S", solidBg: "#7c3aed", softBg: "#ede9fe", softFg: "#5b21b6", softBorder: "#c4b5fd" },
  holiday: { label: "Holiday", short: "Ho", solidBg: "#0284c7", softBg: "#e0f2fe", softFg: "#075985", softBorder: "#7dd3fc" },
};

/** A program with an event date span runs day-by-day, so its sessions read as
 *  "Day 1, Day 2, …" rather than the weekly "Week 1, Week 2, …". */
function isEventProgram(p: Program): boolean {
  return !!p.start_date;
}
/** "Day"/"Week" and "D"/"W" for a program's session unit (event vs weekly). */
function unitLabel(p: Program): string {
  return isEventProgram(p) ? "Day" : "Week";
}
function unitShort(p: Program): string {
  return isEventProgram(p) ? "D" : "W";
}

/** Selected (solid fill, white text) vs unselected (pale tint) button styles. */
function statusStyle(s: AttendanceStatus, selected: boolean): React.CSSProperties {
  const m = STATUS_META[s];
  return selected
    ? { backgroundColor: m.solidBg, color: "#ffffff", borderColor: m.solidBg }
    : { backgroundColor: m.softBg, color: m.softFg, borderColor: m.softBorder };
}

/** Pale badge/cell style for a status (counts, grid cells, legend). */
function softStyle(s: AttendanceStatus): React.CSSProperties {
  return { backgroundColor: STATUS_META[s].softBg, color: STATUS_META[s].softFg };
}

/**
 * The PART-DAY flag on the roll, in the same family as the medical and photo
 * flags beside it: a short badge that reads at a glance, with the full detail
 * in its tooltip.
 *
 * Null unless the child really is booked for less than the whole program —
 * a family who ticked every day is on every roll anyway, so flagging them
 * would be noise. The roll for a given day already hides the children who
 * aren't coming, so this answers the next question a leader asks: which of
 * the children in front of me are not here tomorrow?
 */
function partDayNote(
  program: Program,
  days: readonly string[] | null | undefined,
): { label: string; title: string } | null {
  const all = program.days ?? [];
  if (all.length === 0 || !days || days.length === 0 || days.length >= all.length) return null;
  const list = days.join(", ");
  return {
    label: `${days.length} of ${all.length} days`,
    title: `Booked for these days only: ${list}`,
  };
}

export function AdminAttendance() {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const [programId, setProgramId] = React.useState<number | null>(null);
  const [view, setView] = React.useState<"mark" | "checkin" | "grid" | "report">("mark");
  const [showPast, setShowPast] = React.useState(false);

  const programsQuery = useQuery({
    queryKey: ["admin", "att", "programs"],
    queryFn: () => api.adminListPrograms(),
  });

  const activePrograms = React.useMemo(
    () =>
      (programsQuery.data ?? [])
        .filter((p) => p.active === 1)
        .sort((a, b) => a.sort_order - b.sort_order),
    [programsQuery.data],
  );

  // The picker shows running terms; "Show past terms" folds in the hidden ones
  // (Term 1 / Term 2) so their history can be viewed — active first.
  const pickerPrograms = React.useMemo(() => {
    const all = programsQuery.data ?? [];
    const list = showPast ? [...all] : all.filter((p) => p.active === 1);
    return list.sort((a, b) => b.active - a.active || a.sort_order - b.sort_order);
  }, [programsQuery.data, showPast]);

  // Default to the current (active) term; reset if the selection leaves the list
  // (e.g. after switching "Show past terms" off while on a past term).
  React.useEffect(() => {
    if (pickerPrograms.length === 0) return;
    if (programId !== null && pickerPrograms.some((p) => p.id === programId)) return;
    const first = activePrograms[0] ?? pickerPrograms[0];
    if (first) setProgramId(first.id);
  }, [pickerPrograms, activePrograms, programId]);

  const program = pickerPrograms.find((p) => p.id === programId) ?? null;

  return (
    <section className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-heading text-xl font-bold text-ink">Attendance</h2>
          <p className="mt-1 text-sm text-muted">
            Mark the weekly roll for a running program. Each week is its own lesson, so you can
            track and report attendance per child over the term.
          </p>
        </div>
        <div className="inline-flex rounded-card border border-border p-0.5">
          {(["mark", "checkin", "grid", "report"] as const).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={cn(
                "rounded-[calc(theme(borderRadius.card)-2px)] px-3 py-1.5 text-sm font-semibold transition-colors",
                view === v ? "bg-primary text-primary-fg" : "text-muted hover:text-ink",
              )}
            >
              {v === "mark"
                ? "Mark roll"
                : v === "checkin"
                  ? "Check-in"
                  : v === "grid"
                    ? "Grid"
                    : "Report"}
            </button>
          ))}
        </div>
      </header>

      {programsQuery.isLoading ? (
        <p className="text-sm text-muted">Loading programs…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-4">
            <div className="min-w-[16rem] max-w-sm flex-1 space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-muted">
                Program
              </label>
              <Select
                value={programId != null ? String(programId) : undefined}
                onValueChange={(v) => setProgramId(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a program" />
                </SelectTrigger>
                <SelectContent>
                  {pickerPrograms.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.title}
                      {p.active === 0 ? " · past" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 pb-2 text-sm text-ink">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                checked={showPast}
                onChange={(e) => setShowPast(e.target.checked)}
              />
              Show past terms
            </label>
          </div>

          {pickerPrograms.length === 0 ? (
            <EmptyCard>
              No running programs. Set a program to <strong>Active</strong> in the Programs screen,
              or tick <strong>Show past terms</strong> to view a finished term.
            </EmptyCard>
          ) : program ? (
            view === "mark" ? (
              <MarkRoll key={program.id} api={api} program={program} />
            ) : view === "checkin" ? (
              <div className="space-y-3">
                <div className="flex justify-end">
                  <Link
                    to="/admin/checkin"
                    className={cn(
                      "inline-flex items-center gap-2 rounded-card border border-border bg-card px-3 py-1.5",
                      "text-sm font-semibold text-ink transition-colors hover:bg-paper",
                    )}
                  >
                    <Maximize2 className="h-4 w-4" aria-hidden />
                    Open full-screen kiosk
                  </Link>
                </div>
                <KioskCheckIn key={program.id} api={api} program={program} />
              </div>
            ) : view === "grid" ? (
              <GridView key={program.id} api={api} program={program} />
            ) : (
              <ReportView key={program.id} api={api} program={program} />
            )
          ) : null}
        </>
      )}
    </section>
  );
}

/* ============================ Mark roll ================================= */

function MarkRoll({ api, program }: { api: ApiClient; program: Program }) {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = React.useState<number | null>(null);
  const [yearFilter, setYearFilter] = React.useState<string>("all");
  const [addOpen, setAddOpen] = React.useState(false);

  const [sortBy, setSortBy] = React.useState<"name" | "surname" | "year" | "unmarked">("name");
  const sessionsKey = ["admin", "att", "sessions", program.id] as const;
  const sessionsQuery = useQuery({
    queryKey: sessionsKey,
    queryFn: () => api.adminListSessions(program.id),
  });
  const sessions = sessionsQuery.data ?? [];

  // Open on the current/nearest lesson (today's, else the most recent that has
  // happened, else the next upcoming) rather than the last week of term.
  React.useEffect(() => {
    if (sessions.length === 0) return;
    if (sessionId !== null && sessions.some((s) => s.id === sessionId)) return;
    const def = defaultSessionId(sessions);
    if (def !== null) setSessionId(def);
  }, [sessions, sessionId]);

  const sheetKey = ["admin", "att", "sheet", sessionId] as const;
  const sheetQuery = useQuery({
    queryKey: sheetKey,
    queryFn: () => api.adminGetAttendanceSheet(sessionId as number),
    enabled: sessionId != null,
  });

  const addSession = useMutation({
    mutationFn: () => api.adminAddSession({ program_id: program.id }),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: sessionsKey });
      setSessionId(created.id);
    },
  });

  const mark = useMutation({
    mutationFn: (input: MarkAttendanceInput) => api.adminMarkAttendance(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: sheetKey });
      const prev = qc.getQueryData<AttendanceSheet>(sheetKey);
      if (prev) {
        qc.setQueryData<AttendanceSheet>(sheetKey, {
          ...prev,
          rows: prev.rows.map((r) =>
            r.enrolment_id === input.enrolment_id ? { ...r, status: input.status } : r,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(sheetKey, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: sessionsKey });
    },
  });

  const rows = sheetQuery.data?.rows ?? [];
  const years = React.useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.year_level) set.add(r.year_level);
    return Array.from(set).sort();
  }, [rows]);

  const visibleRows = React.useMemo(
    () => (yearFilter === "all" ? rows : rows.filter((r) => (r.year_level ?? "") === yearFilter)),
    [rows, yearFilter],
  );

  // Group the roster by year level for the "years (group)" view.
  const groups = React.useMemo(() => {
    const map = new Map<string, typeof visibleRows>();
    for (const r of visibleRows) {
      const key = r.year_level || "No year set";
      const list = map.get(key) ?? [];
      list.push(r);
      map.set(key, list);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [visibleRows]);

  // Flat sort (first name or surname A–Z, or unmarked-first) for the
  // non-grouped views. A roll is often called and filed by surname, and the
  // names are stored in one box — so the surname comes from the same rule the
  // spreadsheet columns use (@alight/contracts), not a second guess here.
  const sortedRows = React.useMemo(() => {
    const list = [...visibleRows];
    list.sort((a, b) =>
      sortBy === "surname"
        ? compareByLastName(a.child_name, b.child_name)
        : compareByFirstName(a.child_name, b.child_name),
    );
    if (sortBy === "unmarked") {
      list.sort((a, b) => (a.status ? 1 : 0) - (b.status ? 1 : 0));
    }
    return list;
  }, [visibleRows, sortBy]);

  // One roster row — reused by the grouped (by year) and flat (by name) views.
  // `showYear` tags the year inline when the rows aren't grouped under a heading.
  const studentRow = (r: (typeof rows)[number], showYear: boolean) => {
    // What the family actually excluded, so a helper with a camera knows "not
    // on social media" from "no photos at all". Records that predate the
    // specific questions only carry the coarse flag.
    const photoNote =
      describePhotoExclusions(r.photo_exclusions, r.photo_exclude_note) ||
      (r.photo_consent === 0 ? "No photos" : "");
    // Part-day: this child is only booked for some of the program's days. The
    // sheet already hides them on the days they didn't book, so the flag is
    // there to say "not here all week" — flagged only when the booking really
    // is short of the whole program.
    const partDays = partDayNote(program, r.days);
    return (
    <TableRow key={r.enrolment_id}>
      <TableCell>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium text-ink">{r.child_name}</span>
          {showYear && r.year_level ? (
            <span className="rounded-full bg-paper px-2 py-0.5 text-xs font-medium text-muted">
              {r.year_level}
            </span>
          ) : null}
          {r.allergies ? (
            <span
              title={`Medical / allergies: ${r.allergies}`}
              style={{ backgroundColor: "#fee2e2", color: "#991b1b" }}
              className="inline-flex max-w-[18rem] items-center gap-1 truncate rounded-full px-2 py-0.5 text-xs font-semibold"
            >
              <AlertTriangle className="h-3 w-3 flex-shrink-0" aria-hidden />
              <span className="truncate">{r.allergies}</span>
            </span>
          ) : null}
          {photoNote ? (
            <span
              title={photoNote}
              style={{ backgroundColor: "#e2e8f0", color: "#334155" }}
              className="inline-flex max-w-[18rem] items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
            >
              <CameraOff className="h-3 w-3 flex-shrink-0" aria-hidden />
              <span className="truncate">{photoNote}</span>
            </span>
          ) : null}
          {partDays ? (
            <span
              title={partDays.title}
              style={{ backgroundColor: "#ccfbf1", color: "#115e59" }}
              className="inline-flex max-w-[18rem] items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
            >
              <CalendarCheck className="h-3 w-3 flex-shrink-0" aria-hidden />
              <span className="truncate">{partDays.label}</span>
            </span>
          ) : null}
        </div>
        <div className="text-xs text-muted">{r.parent_name ?? ""}</div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap justify-end gap-1">
          {STATUS_ORDER.map((s) => {
            const on = r.status === s;
            return (
              <button
                key={s}
                type="button"
                aria-pressed={on}
                style={statusStyle(s, on)}
                onClick={() =>
                  mark.mutate({
                    session_id: sessionId as number,
                    enrolment_id: r.enrolment_id,
                    // Tapping the active status again clears it.
                    status: on ? null : s,
                  })
                }
                className={cn(
                  "rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors",
                  on && "shadow-sm",
                )}
              >
                {STATUS_META[s].label}
              </button>
            );
          })}
        </div>
      </TableCell>
    </TableRow>
    );
  };

  if (sessionsQuery.isLoading) return <p className="text-sm text-muted">Loading lessons…</p>;

  // No lessons yet — explain why and offer to start.
  if (sessions.length === 0) {
    const anchorDate = program.start_date ?? program.first_lesson_date;
    return (
      <EmptyCard>
        {!anchorDate ? (
          <>
            No {isEventProgram(program) ? "days" : "lessons"} yet. For a weekly program set a{" "}
            <strong>lesson day</strong> and <strong>first lesson date</strong>; for a special
            program set the <strong>event start &amp; end dates</strong> (Programs screen) and the
            sessions appear here automatically.
          </>
        ) : (
          <div className="space-y-3">
            <p>
              No {isEventProgram(program) ? "days" : "lessons"} yet —{" "}
              {isEventProgram(program) ? "the event starts" : "the first lesson is"}{" "}
              {formatSessionDate(anchorDate)}. Sessions are created automatically
              {isEventProgram(program) ? " for each day of the event" : " once that date arrives"},
              or you can add the first one now to start marking early.
            </p>
            <Button size="sm" onClick={() => addSession.mutate()} disabled={addSession.isPending}>
              <CalendarPlus className="h-4 w-4" />
              {addSession.isPending
                ? "Adding…"
                : `Add first ${isEventProgram(program) ? "day" : "lesson"}`}
            </Button>
          </div>
        )}
      </EmptyCard>
    );
  }

  const activeSession = sessions.find((s) => s.id === sessionId) ?? null;

  return (
    <div className="space-y-4">
      {/* Lesson picker + filters */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1 space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted">Lesson</label>
          <Select
            value={sessionId != null ? String(sessionId) : undefined}
            onValueChange={(v) => setSessionId(Number(v))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose a lesson" />
            </SelectTrigger>
            <SelectContent>
              {sessions.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {unitLabel(program)} {s.week_number} — {formatSessionDate(s.session_date)}
                  {s.status === "cancelled" ? " (cancelled)" : ""} · {s.marked_count}/
                  {s.roster_count} marked
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[10rem] space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted">Year</label>
          <Select value={yearFilter} onValueChange={setYearFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All years</SelectItem>
              {years.map((y) => (
                <SelectItem key={y} value={y}>
                  {y}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="min-w-[10rem] space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted">Sort by</label>
          <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">First name (A–Z)</SelectItem>
              <SelectItem value="surname">Surname (A–Z)</SelectItem>
              <SelectItem value="year">Year group</SelectItem>
              <SelectItem value="unmarked">Unmarked first</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => addSession.mutate()}
          disabled={addSession.isPending}
          title={
            isEventProgram(program)
              ? "Add the next day of the event"
              : "Prepare next week's lesson ahead of time"
          }
        >
          <CalendarPlus className="h-4 w-4" />
          {addSession.isPending
            ? "Adding…"
            : isEventProgram(program)
              ? "Add next day"
              : "Add next lesson"}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={() => setAddOpen(true)}
          title="Enrol a walk-up or a returning child onto this program"
        >
          <UserPlus className="h-4 w-4" />
          Add child
        </Button>
      </div>

      <ManualEnrolmentDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        program={program}
        onCreated={() => qc.invalidateQueries({ queryKey: ["admin", "att"] })}
      />

      {activeSession && (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
          <span>
            {activeSession.marked_count}/{activeSession.roster_count} marked
          </span>
          {/* Say WHY the list is shorter than the program's total, so a
              missing name reads as "not booked today", never as a bug. */}
          {hasDayChoice(program) ? (
            <span
              title={
                sessionDayLabel(program.days, activeSession.week_number) ??
                "Only the children booked for this day are listed"
              }
              style={{ backgroundColor: "#ccfbf1", color: "#115e59" }}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
            >
              <CalendarCheck className="h-3 w-3 flex-shrink-0" aria-hidden />
              Booked for this day only
            </span>
          ) : null}
          {STATUS_ORDER.map((s) =>
            activeSession.counts[s] ? (
              <span
                key={s}
                style={softStyle(s)}
                className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold"
              >
                {activeSession.counts[s]} {STATUS_META[s].label.toLowerCase()}
              </span>
            ) : null,
          )}
        </div>
      )}

      {sheetQuery.isLoading ? (
        <p className="text-sm text-muted">Loading roster…</p>
      ) : rows.length === 0 ? (
        <EmptyCard>
          {hasDayChoice(program)
            ? "No children are booked for this day. Families on this program pick the days they are coming, so each day has its own list."
            : "No children enrolled in this program yet. Once families enrol (or you import them), their names will appear here to mark."}
        </EmptyCard>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Child</TableHead>
                  <TableHead className="text-right">Mark</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortBy === "year"
                  ? groups.map(([year, groupRows]) => (
                      <React.Fragment key={year}>
                        <TableRow className="bg-paper hover:bg-paper">
                          <TableCell
                            colSpan={2}
                            className="py-1.5 text-xs font-bold uppercase tracking-wide text-muted"
                          >
                            {year} · {groupRows.length}
                          </TableCell>
                        </TableRow>
                        {groupRows.map((r) => studentRow(r, false))}
                      </React.Fragment>
                    ))
                  : sortedRows.map((r) => studentRow(r, true))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ============================= Check-in =============================== */

/** Tablet kiosk core. The host sets the lesson and hands the tablet over; each
 *  child taps their own name to mark themselves Here. Uses the same lesson +
 *  mark endpoint as the roll, limited to self check-in. Reused by the Check-in
 *  tab and the full-screen /admin/checkin page. */
export function KioskCheckIn({ api, program }: { api: ApiClient; program: Program }) {
  const qc = useQueryClient();
  const [sessionId, setSessionId] = React.useState<number | null>(null);
  const [search, setSearch] = React.useState("");

  const sessionsKey = ["admin", "att", "sessions", program.id] as const;
  const sessionsQuery = useQuery({
    queryKey: sessionsKey,
    queryFn: () => api.adminListSessions(program.id),
  });
  const sessions = sessionsQuery.data ?? [];

  React.useEffect(() => {
    if (sessions.length === 0) return;
    if (sessionId !== null && sessions.some((s) => s.id === sessionId)) return;
    const def = defaultSessionId(sessions);
    if (def !== null) setSessionId(def);
  }, [sessions, sessionId]);

  const sheetKey = ["admin", "att", "sheet", sessionId] as const;
  const sheetQuery = useQuery({
    queryKey: sheetKey,
    queryFn: () => api.adminGetAttendanceSheet(sessionId as number),
    enabled: sessionId != null,
  });

  const mark = useMutation({
    mutationFn: (input: MarkAttendanceInput) => api.adminMarkAttendance(input),
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: sheetKey });
      const prev = qc.getQueryData<AttendanceSheet>(sheetKey);
      if (prev) {
        qc.setQueryData<AttendanceSheet>(sheetKey, {
          ...prev,
          rows: prev.rows.map((r) =>
            r.enrolment_id === input.enrolment_id ? { ...r, status: input.status } : r,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(sheetKey, ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: sessionsKey }),
  });

  const rows = sheetQuery.data?.rows ?? [];
  const q = search.trim().toLowerCase();
  const filtered = React.useMemo(
    () =>
      rows
        .filter((r) => !q || r.child_name.toLowerCase().includes(q))
        .sort((a, b) => a.child_name.localeCompare(b.child_name)),
    [rows, q],
  );
  const hereCount = rows.filter((r) => r.status === "here" || r.status === "late").length;
  const activeSession = sessions.find((s) => s.id === sessionId) ?? null;

  if (sessionsQuery.isLoading) return <p className="text-sm text-muted">Loading lessons…</p>;
  if (sessions.length === 0) {
    return (
      <EmptyCard>No lessons yet for this program — add one on the Mark roll tab first.</EmptyCard>
    );
  }

  return (
    <div className="space-y-4">
      {/* Host set-up bar */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-[15rem] flex-1 space-y-1.5">
          <label className="text-xs font-semibold uppercase tracking-wide text-muted">
            Lesson — set this, then hand over the tablet
          </label>
          <Select
            value={sessionId != null ? String(sessionId) : undefined}
            onValueChange={(v) => setSessionId(Number(v))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Choose a lesson" />
            </SelectTrigger>
            <SelectContent>
              {sessions.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  {unitLabel(program)} {s.week_number} — {formatSessionDate(s.session_date)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="rounded-card border border-border bg-card px-4 py-2 text-center">
          <div className="font-heading text-2xl font-bold text-ink">
            {hereCount}
            <span className="text-base font-medium text-muted"> / {rows.length}</span>
          </div>
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">Checked in</div>
        </div>
      </div>

      {/* Kiosk area */}
      <Card>
        <CardContent className="space-y-5 p-5">
          <div className="text-center">
            <h3 className="font-heading text-2xl font-bold text-ink">Tap your name to check in</h3>
            <p className="mt-1 text-sm text-muted">
              {program.title}
              {activeSession ? ` · ${formatSessionDate(activeSession.session_date)}` : ""}
            </p>
          </div>

          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search your name…"
            className="mx-auto h-12 max-w-md text-center text-lg"
            aria-label="Search for your name"
          />

          {sheetQuery.isLoading ? (
            <p className="text-center text-sm text-muted">Loading names…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center text-sm text-muted">
              {rows.length === 0 ? "No children enrolled yet." : "No names match your search."}
            </p>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {filtered.map((r) => {
                const here = r.status === "here" || r.status === "late";
                return (
                  <button
                    key={r.enrolment_id}
                    type="button"
                    onClick={() =>
                      mark.mutate({
                        session_id: sessionId as number,
                        enrolment_id: r.enrolment_id,
                        status: r.status === "here" ? null : "here",
                      })
                    }
                    style={
                      here ? { backgroundColor: "#16a34a", borderColor: "#16a34a", color: "#fff" } : undefined
                    }
                    className={cn(
                      "relative flex min-h-[3.25rem] items-center justify-center rounded-card border px-3 py-2 text-center transition-colors",
                      here ? "shadow-sm" : "border-border bg-card text-ink hover:bg-paper",
                    )}
                  >
                    {here ? (
                      <Check className="absolute right-1.5 top-1.5 h-4 w-4" aria-hidden />
                    ) : null}
                    <span className="text-base font-bold leading-tight">{r.child_name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* =============================== Grid ================================== */

/** Read-only whole-term matrix: children down the side, lessons across the top,
 *  every recorded mark shown at once (like the paper roll / spreadsheet). */
function GridView({ api, program }: { api: ApiClient; program: Program }) {
  const [yearFilter, setYearFilter] = React.useState<string>("all");
  const [sortBy, setSortBy] = React.useState<"name" | "surname" | "year" | "rate">("name");
  const gridQuery = useQuery({
    queryKey: ["admin", "att", "grid", program.id],
    queryFn: () => api.adminAttendanceGrid(program.id),
  });

  const sessions = gridQuery.data?.sessions ?? [];
  const students = gridQuery.data?.students ?? [];

  const years = React.useMemo(() => {
    const set = new Set<string>();
    for (const s of students) if (s.year_level) set.add(s.year_level);
    return Array.from(set).sort();
  }, [students]);

  // On a part-day program a child is only counted on the days they booked —
  // both in the grid cells and in the rate beside them.
  const booked = React.useCallback(
    (student: (typeof students)[number], weekNumber: number) =>
      isBookedForSession(program.days, weekNumber, student.days),
    [program.days],
  );

  const rate = React.useCallback(
    (student: (typeof students)[number]) => {
      const mine = sessions.filter((s) => booked(student, s.week_number));
      if (mine.length === 0) return 0;
      const attended = mine.reduce((n, s) => {
        const st = student.marks[String(s.id)];
        return n + (st === "here" || st === "late" ? 1 : 0);
      }, 0);
      return Math.round((attended / mine.length) * 100);
    },
    [sessions, booked],
  );

  const visible = React.useMemo(() => {
    const list =
      yearFilter === "all"
        ? [...students]
        : students.filter((s) => (s.year_level ?? "") === yearFilter);
    list.sort((a, b) =>
      sortBy === "surname"
        ? compareByLastName(a.child_name, b.child_name)
        : compareByFirstName(a.child_name, b.child_name),
    );
    if (sortBy === "year") {
      list.sort((a, b) => (a.year_level ?? "").localeCompare(b.year_level ?? ""));
    } else if (sortBy === "rate") {
      list.sort((a, b) => rate(a) - rate(b));
    }
    return list;
  }, [students, yearFilter, sortBy, rate]);

  function exportCsv() {
    downloadCsv(
      `attendance-grid-${program.term || program.title}`,
      [
        // Was keyed "student" while the heading said "child", so every name
        // came out of this file BLANK. Keys and headings must match — the CSV
        // builder looks each cell up by its heading.
        "child",
        "first_name",
        "last_name",
        "year",
        ...sessions.map((s) => `${unitShort(program)}${s.week_number}`),
        "attendance",
      ],
      visible.map((st) => ({
        child: st.child_name,
        first_name: splitPersonName(st.child_name).first,
        last_name: splitPersonName(st.child_name).last,
        year: st.year_level ?? "",
        ...Object.fromEntries(
          sessions.map((s) => [
            `${unitShort(program)}${s.week_number}`,
            booked(st, s.week_number) ? (st.marks[String(s.id)] ?? "") : "not booked",
          ]),
        ),
        attendance: `${rate(st)}%`,
      })),
    );
  }

  if (gridQuery.isLoading) return <p className="text-sm text-muted">Loading grid…</p>;
  if (sessions.length === 0) return <EmptyCard>No lessons for this program yet.</EmptyCard>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[9rem] space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted">Year</label>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {years.map((y) => (
                  <SelectItem key={y} value={y}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[9rem] space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted">
              Sort by
            </label>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">First name (A–Z)</SelectItem>
                <SelectItem value="surname">Surname (A–Z)</SelectItem>
                <SelectItem value="year">Year group</SelectItem>
                <SelectItem value="rate">Attendance (low first)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Legend */}
          <div className="flex flex-wrap items-center gap-1.5 pb-1">
            {STATUS_ORDER.map((s) => (
              <span
                key={s}
                style={softStyle(s)}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
              >
                <span className="font-mono">{STATUS_META[s].short}</span>
                {STATUS_META[s].label}
              </span>
            ))}
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={visible.length === 0}>
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {visible.length === 0 ? (
        <EmptyCard>No children to show for this term.</EmptyCard>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="sticky left-0 z-10 bg-card px-3 py-2 text-left font-semibold text-ink">
                    Child
                  </th>
                  {sessions.map((s) => (
                    <th
                      key={s.id}
                      title={formatSessionDate(s.session_date)}
                      className="whitespace-nowrap px-2 py-2 text-center text-xs font-semibold text-muted"
                    >
                      {unitShort(program)}
                      {s.week_number}
                      <div className="font-normal">{shortDate(s.session_date)}</div>
                    </th>
                  ))}
                  <th className="px-3 py-2 text-center text-xs font-semibold text-muted">Att.</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((st) => (
                  <tr key={st.enrolment_id} className="border-b border-border/60">
                    <td className="sticky left-0 z-10 bg-card px-3 py-1.5 text-ink">
                      <div className="font-medium">{st.child_name}</div>
                      <div className="text-xs text-muted">{st.year_level ?? ""}</div>
                    </td>
                    {sessions.map((s) => {
                      const status = st.marks[String(s.id)];
                      if (!booked(st, s.week_number)) {
                        return (
                          <td key={s.id} className="px-1 py-1 text-center">
                            <span
                              title="Not booked for this day"
                              className="inline-flex h-6 w-7 items-center justify-center rounded bg-paper text-xs font-bold text-muted"
                            >
                              –
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td key={s.id} className="px-1 py-1 text-center">
                          {status ? (
                            <span
                              title={STATUS_META[status].label}
                              style={softStyle(status)}
                              className="inline-flex h-6 w-7 items-center justify-center rounded text-xs font-bold"
                            >
                              {STATUS_META[status].short}
                            </span>
                          ) : (
                            <span className="text-border">·</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-3 py-1.5 text-center font-semibold text-ink">{rate(st)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/* ============================== Report ================================= */

function ReportView({ api, program }: { api: ApiClient; program: Program }) {
  const [yearFilter, setYearFilter] = React.useState<string>("all");
  const [sortBy, setSortBy] = React.useState<"name" | "surname" | "year" | "rate">("name");
  const summaryQuery = useQuery({
    queryKey: ["admin", "att", "summary", program.id],
    queryFn: () => api.adminAttendanceSummary(program.id),
  });

  const students = summaryQuery.data?.students ?? [];
  const sessions = summaryQuery.data?.sessions ?? [];
  const scheduled = sessions.filter((s) => s.status === "scheduled").length;

  const years = React.useMemo(() => {
    const set = new Set<string>();
    for (const s of students) if (s.year_level) set.add(s.year_level);
    return Array.from(set).sort();
  }, [students]);

  const visible = React.useMemo(() => {
    const list =
      yearFilter === "all"
        ? [...students]
        : students.filter((s) => (s.year_level ?? "") === yearFilter);
    list.sort((a, b) =>
      sortBy === "surname"
        ? compareByLastName(a.child_name, b.child_name)
        : compareByFirstName(a.child_name, b.child_name),
    );
    if (sortBy === "year") {
      list.sort((a, b) => (a.year_level ?? "").localeCompare(b.year_level ?? ""));
    } else if (sortBy === "rate") {
      // Lowest attendance first — surfaces the children to follow up on.
      list.sort((a, b) => a.attendance_rate - b.attendance_rate);
    }
    return list;
  }, [students, yearFilter, sortBy]);

  const overallRate =
    visible.length > 0
      ? Math.round(visible.reduce((a, s) => a + s.attendance_rate, 0) / visible.length)
      : 0;

  function exportCsv() {
    downloadCsv(
      `attendance-${program.term || program.title}`,
      // Same heading/key fix as the grid export above.
      [
        "child",
        "first_name",
        "last_name",
        "year",
        ...STATUS_ORDER,
        "marked",
        "lessons",
        "attendance_rate",
      ],
      visible.map((s) => ({
        child: s.child_name,
        first_name: splitPersonName(s.child_name).first,
        last_name: splitPersonName(s.child_name).last,
        year: s.year_level ?? "",
        here: s.counts.here ?? 0,
        late: s.counts.late ?? 0,
        away: s.counts.away ?? 0,
        sick: s.counts.sick ?? 0,
        holiday: s.counts.holiday ?? 0,
        marked: s.marked,
        lessons: s.total_sessions,
        attendance_rate: `${s.attendance_rate}%`,
      })),
    );
  }

  if (summaryQuery.isLoading) return <p className="text-sm text-muted">Loading report…</p>;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label="Lessons held" value={String(scheduled)} />
        <StatTile label="Children" value={String(visible.length)} />
        <StatTile label="Avg attendance" value={`${overallRate}%`} />
        <StatTile
          label="Marks recorded"
          value={String(visible.reduce((a, s) => a + s.marked, 0))}
        />
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[10rem] space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted">Year</label>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All years</SelectItem>
                {years.map((y) => (
                  <SelectItem key={y} value={y}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[10rem] space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wide text-muted">
              Sort by
            </label>
            <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">First name (A–Z)</SelectItem>
                <SelectItem value="surname">Surname (A–Z)</SelectItem>
                <SelectItem value="year">Year group</SelectItem>
                <SelectItem value="rate">Attendance (low first)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={exportCsv} disabled={visible.length === 0}>
          <Download className="h-4 w-4" />
          Export CSV
        </Button>
      </div>

      {visible.length === 0 ? (
        <EmptyCard>No children to report on for this program yet.</EmptyCard>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Child</TableHead>
                  <TableHead>Year</TableHead>
                  {STATUS_ORDER.map((s) => (
                    <TableHead key={s} className="text-center">
                      {STATUS_META[s].label}
                    </TableHead>
                  ))}
                  <TableHead className="text-right">Attendance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((s) => (
                  <ReportRow key={s.enrolment_id} student={s} />
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function ReportRow({ student }: { student: AttendanceStudentReport }) {
  return (
    <TableRow>
      <TableCell className="font-medium text-ink">{student.child_name}</TableCell>
      <TableCell className="whitespace-nowrap text-muted">{student.year_level ?? "—"}</TableCell>
      {STATUS_ORDER.map((s) => (
        <TableCell key={s} className="text-center text-ink">
          {student.counts[s] ?? 0}
        </TableCell>
      ))}
      <TableCell className="text-right">
        <span
          className="font-semibold"
          style={{
            color:
              student.attendance_rate >= 80
                ? "#15803d"
                : student.attendance_rate >= 50
                  ? "#b45309"
                  : "#b91c1c",
          }}
        >
          {student.attendance_rate}%
        </span>
        <span className="ml-1 text-xs text-muted">
          ({student.marked}/{student.total_sessions})
        </span>
      </TableCell>
    </TableRow>
  );
}

/* ============================== helpers ================================ */

type ApiClient = ReturnType<typeof apiClient>;

function StatTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-card border border-border bg-card p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="mt-1 font-heading text-2xl font-bold text-ink">{value}</div>
    </div>
  );
}

function EmptyCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-card border border-border bg-paper px-4 py-8 text-center text-sm text-muted">
      {children}
    </div>
  );
}

/** The lesson to open on by default: today's lesson if there is one, else the
 *  most recent lesson that's already happened, else the nearest upcoming one
 *  (before the term starts). `sessions` arrive newest-date first. */
function defaultSessionId(sessions: { id: number; session_date: string }[]): number | null {
  if (sessions.length === 0) return null;
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
  // Newest-first, so the first lesson on/before today is the most recent one.
  const currentOrPast = sessions.find((s) => s.session_date <= today);
  const chosen = currentOrPast ?? sessions[sessions.length - 1];
  return chosen ? chosen.id : null;
}

/** Compact date for tight grid headers, e.g. "24 Jul". */
function shortDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", timeZone: "UTC" });
}

/** Format an ISO date (YYYY-MM-DD) as e.g. "Fri 24 Jul 2026". */
function formatSessionDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Build a CSV string and trigger a client-side download. */
function downloadCsv(
  name: string,
  columns: string[],
  rows: Array<Record<string, string | number>>,
): void {
  const escape = (value: string | number): string => {
    const s = String(value);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = columns.map(escape).join(",");
  const body = rows.map((row) => columns.map((col) => escape(row[col] ?? "")).join(",")).join("\n");
  const csv = `﻿${header}\n${body}`;
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `alight-${name.replace(/\s+/g, "-").toLowerCase()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
